import * as React from "react";
import { Button, Stack, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import Box from "@mui/material/Box";
import { currencyFormatter } from "utilities/currencyFormatter";
import { setPropertyId, setCurrentTab } from "state";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: "#f1f1f5",
		color: "#000",
		fontSize: 15,
		fontWeight: 700,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 16,
	},
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(odd)": {
		backgroundColor: "white",
	},
	// // hide last border
	// "&:last-child td, &:last-child th": {
	// 	border: 0,
	// },
}));


function TablePaginationActions(props) {
	const theme = useTheme();
	const { count, page, rowsPerPage, onPageChange } = props;

	const handleFirstPageButtonClick = (event) => {
		onPageChange(event, 0);
	};

	const handleBackButtonClick = (event) => {
		onPageChange(event, page - 1);
	};

	const handleNextButtonClick = (event) => {
		onPageChange(event, page + 1);
	};

	const handleLastPageButtonClick = (event) => {
		onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 2.5 }}>
			<IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
				{theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
			</IconButton>
			<IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
				{theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
			</IconButton>
			<IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
				{theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
			</IconButton>
			<IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
				{theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
			</IconButton>
		</Box>
	);
}

export default function DashboardPaymentsTable(props) {
	const {rows}= props
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Group arrears by property and calculate summaries
	const propertySummaries = React.useMemo(() => {
		if (!rows || rows.length === 0) return [];

		const propertyMap = {};

		rows.forEach((row) => {
			// Get property ID from occupancy_details or use property_name as key
			const propertyId = row.occupancy_details?.related_rental_unit?.related_rental?.id || null;
			const propertyName = row.property_name || 'Unknown Property';
			const key = propertyId || propertyName;

			if (!propertyMap[key]) {
				propertyMap[key] = {
					propertyId: propertyId,
					propertyName: propertyName,
					totalAmountInArrears: 0,
					tenantsInArrears: new Set(),
					totalRecords: 0,
				};
			}

			propertyMap[key].totalAmountInArrears += parseFloat(row.amount_in_arrears || 0);
			if (row.tenant_name) {
				propertyMap[key].tenantsInArrears.add(row.tenant_name);
			}
			propertyMap[key].totalRecords += 1;
		});

		// Convert to array and calculate tenant count
		return Object.values(propertyMap).map((summary) => ({
			...summary,
			tenantsInArrears: summary.tenantsInArrears.size,
		}));
	}, [rows]);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleNavigateToPropertyArrears = (propertyId) => {
		if (propertyId) {
			dispatch(setPropertyId(propertyId));
			dispatch(setCurrentTab({ name: "propertyDetailsTab", tab: 6 })); // 6 is the Rent Arrears tab index
			navigate("/propertyDetailsTab");
		}
	};

	return (
		<>
			<TableContainer sx={{ display: { xs: "none", md: "block" } }} component={Paper}>
				<Table  aria-label="customized table">
					<TableHead>
						<TableRow>
							<StyledTableCell>Property Name</StyledTableCell>
							<StyledTableCell align="center">Amount </StyledTableCell>
							<StyledTableCell align="center">Tenants </StyledTableCell>
							<StyledTableCell align="center">Actions</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{props.isLoadingRentals ? (
							<StyledTableRow></StyledTableRow>
						) : (
							<>
								{(propertySummaries.length > 0 ? propertySummaries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((summary, index) => (
									<StyledTableRow key={summary.propertyId || summary.propertyName + index}>
										<StyledTableCell component="th" scope="row">
											{summary.propertyName}
										</StyledTableCell>
										<StyledTableCell align="center">
											{currencyFormatter(Math.round(summary.totalAmountInArrears))}
										</StyledTableCell>
										<StyledTableCell align="center">{summary.tenantsInArrears}</StyledTableCell>
										<StyledTableCell align="center">
											{summary.propertyId ? (
												<Button size="small" onClick={() => handleNavigateToPropertyArrears(summary.propertyId)}>
													<h4 style={{ color: "#2094FF", margin: "0 0 0 0" }}>View Arrears</h4>
												</Button>
											) : (
												<span style={{ color: "#666", fontSize: "14px" }}>N/A</span>
											)}
										</StyledTableCell>
									</StyledTableRow>
								)):(
									<StyledTableRow>
										<StyledTableCell component="th" scope="row" colSpan={4}>
											No Data Available
										</StyledTableCell>
									</StyledTableRow>
								))}
							</>
						)}
					</TableBody>
				</Table>
				<TablePagination
					component="div"
					colSpan={12}
					count={propertySummaries.length}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					labelRowsPerPage="Rows per page" // Custom label for "Rows per page"
					sx={{
						// Styles for the TablePagination component itself
						"& .MuiInputBase-root.MuiInputBase-colorPrimary.MuiTablePagination-input.css-baf1rs-MuiInputBase-root-MuiTablePagination-select": {
							marginRight: "5px",
						},
						"& .MuiTablePagination-actions": {
							marginLeft: 0,
						},
						"& .MuiTablePagination-displayedRows": {
							fontSize: "1rem",
							margin: 0,
						},
						"& .MuiSelect-select.MuiInputBase-input": {
							fontSize: "1rem",
							marginTop: "10px",
						},
						"& .MuiTablePagination-selectLabel": {
							fontSize: "1rem",
						},
						"& .MuiInputBase-root-MuiTablePagination-select .MuiTablePagination-select": {
							marginTop: "4px",
						},
					}}
				/>
			</TableContainer>

			{/* MOBILE */}
			<TableContainer sx={{ display: { xs: "block", md: "none" } }} component={Paper}>
				<Table aria-label="customized table">
					<TableHead>
						<TableRow>
							<StyledTableCell>Property Name</StyledTableCell>
							<StyledTableCell align="left">Actions</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{props.isLoadingRentals ? (
							<StyledTableRow></StyledTableRow>
						) : (
							<>
								{propertySummaries.length > 0 ? (
									propertySummaries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((summary, index) => (
										<StyledTableRow key={summary.propertyId || summary.propertyName + index}>
											<StyledTableCell align="left">
												<span>
													<strong>{summary.propertyName}</strong>
												</span>
												<Stack spacing={1} style={{ fontSize: "16px", marginTop: "7px" }}>
													<span>
														<strong>Amount in Arrears:</strong> {currencyFormatter(Math.round(summary.totalAmountInArrears))}
													</span>
													<span>
														<strong>Tenants in Arrears:</strong> {summary.tenantsInArrears}
													</span>
													<span>
														<strong>Total Records:</strong> {summary.totalRecords}
													</span>
												</Stack>
											</StyledTableCell>
											<StyledTableCell align="center">
												{summary.propertyId ? (
													<Button size="small" onClick={() => handleNavigateToPropertyArrears(summary.propertyId)}>
														<h4 style={{ color: "#2094FF", margin: "0 0 0 0" }}>View Arrears</h4>
													</Button>
												) : (
													<span style={{ color: "#666", fontSize: "14px" }}>N/A</span>
												)}
											</StyledTableCell>
										</StyledTableRow>
									))
								) : (
									<StyledTableRow>
										<StyledTableCell component="th" scope="row" colSpan={2}>
											No Data Available
										</StyledTableCell>
									</StyledTableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
				<TablePagination
					component="div"
					count={propertySummaries.length}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					labelRowsPerPage="Rows" // Custom label for "Rows per page"
					sx={{
						// Styles for the TablePagination component itself
						"& .MuiInputBase-root.MuiInputBase-colorPrimary.MuiTablePagination-input.css-baf1rs-MuiInputBase-root-MuiTablePagination-select": {
							marginRight: "5px",
						},
						"& .MuiTablePagination-actions": {
							marginLeft: 0,
						},
						"& .MuiTablePagination-displayedRows": {
							fontSize: "0.95rem",
							margin: 0,
						},
						"& .MuiSelect-select.MuiInputBase-input": {
							fontSize: "0.95rem",
							marginTop: "5px",
						},
						"& .MuiTablePagination-selectLabel": {
							fontSize: "0.95rem",
						},
						"& .MuiInputBase-root-MuiTablePagination-select .MuiTablePagination-select": {
							marginTop: "4px",
						},
					}}
				/>
			</TableContainer>
		</>
	);
}
